import { Link } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import { profile } from "@/mocks/profile";
import { worksList } from "@/mocks/works";

export default function ProfilePage() {
  usePageMeta({
    title: "大岛优香个人资料 - 生日、演艺经历与作品",
    description:
      "大岛优香个人资料专区，包含生日、国籍、职业等基本信息、完整演艺经历与主要影视作品，全面了解她的成长与成就。",
    keywords: "大岛优香个人资料,大岛优香生日,演艺经历,明星资料",
  });

  const basicInfo = [
    { label: "出生日期", value: profile.birthday },
    { label: "国籍", value: profile.nationality },
    { label: "职业", value: profile.occupation },
    { label: "身高", value: profile.height },
    { label: "出道年份", value: profile.debut },
    { label: "经纪公司", value: profile.agency },
  ];

  const featuredWorks = worksList.slice(0, 4);

  return (
    <div className="w-full px-4 md:px-6 py-8 md:py-10">
      <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6">
        <Link to="/" className="hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
        <span className="text-foreground-800">个人资料</span>
      </nav>

      <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8">
        个人资料
      </h1>

      {/* Hero card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1">
          <div className="w-full aspect-[4/5] rounded-lg overflow-hidden bg-background-200">
            <img
              src={profile.avatar}
              alt={profile.name}
              title={`${profile.name}个人形象照`}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        <div className="lg:col-span-2">
          <p className="text-sm tracking-[0.3em] text-primary-600 mb-2">{profile.nameEn}</p>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
            {profile.name}
          </h2>
          <p className="text-sm text-foreground-600 mb-5">{profile.tagline}</p>
          <p className="text-[15px] text-foreground-800 leading-8 mb-6">{profile.intro}</p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {profile.stats.map((s) => (
              <div
                key={s.label}
                className="bg-background-100 border border-background-200 rounded-lg p-4 text-center"
              >
                <p className="font-heading text-xl font-bold text-primary-600">{s.value}</p>
                <p className="text-xs text-foreground-500 mt-1">{s.label}</p>
              </div>
            ))}
          </div>

          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {basicInfo.map((item) => (
              <div
                key={item.label}
                className="flex items-center gap-2 text-sm py-2 border-b border-background-200"
              >
                <dt className="text-foreground-500 shrink-0 w-20">{item.label}</dt>
                <dd className="text-foreground-900">{item.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      {/* 演艺经历 */}
      <section className="mt-12">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
          演艺经历
        </h2>
        <div className="relative pl-6 border-l-2 border-background-200 space-y-6">
          {profile.experiences.map((exp) => (
            <div key={exp.year} className="relative">
              <span className="absolute -left-[31px] top-1 w-3 h-3 rounded-full bg-accent-500 ring-4 ring-accent-100" />
              <p className="font-heading font-bold text-primary-600 mb-1">{exp.year}</p>
              <p className="text-sm text-foreground-700 leading-relaxed">{exp.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 社交媒体 */}
      <section className="mt-12">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
          社交媒体
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {profile.socials.map((s) => (
            <a
              key={s.name}
              href={s.url}
              className="flex items-center gap-3 bg-background-100 border border-background-200 rounded-lg p-4 hover:border-primary-300 transition-colors cursor-pointer"
            >
              <span className="w-10 h-10 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
                <i className={`${s.icon} text-lg`} />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-foreground-900">{s.name}</span>
                <span className="block text-xs text-foreground-500 truncate">{s.handle}</span>
              </span>
            </a>
          ))}
        </div>
        <p className="text-xs text-foreground-400 mt-3">（以上链接为演示示例，不代表真实官方账号）</p>
      </section>

      {/* 主要作品 */}
      <section className="mt-12">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
            主要影视作品
          </h2>
          <Link
            to="/works"
            className="text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap flex items-center gap-1"
          >
            查看全部
            <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {featuredWorks.map((work) => (
            <Link
              key={work.id}
              to={`/works/${work.id}`}
              className="group bg-background-100 border border-background-200 rounded-lg p-4 hover:border-primary-300 transition-colors cursor-pointer"
            >
              <p className="font-heading font-bold text-foreground-950 mb-1 group-hover:text-primary-600 transition-colors">
                {work.title}
              </p>
              <p className="text-xs text-foreground-500">
                {work.type} · {work.year}
              </p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}